import { createInstance } from '@module-federation/runtime';
import angular from 'angular';

// Define a simple interface for the service
export interface IModuleFederationService {
    getRuntime(): any; // The return type of createInstance is complex, so 'any' is fine here
    registerRemotes(remotes: { name: string; entry: string }[]): Promise<void>;
    getRemotesVersion(): any;
    loadRemoteModule(moduleName: string, exposedModule: string, ecvModule: string): Promise<void>;
}

export class ModuleFederationService implements IModuleFederationService {
    private runtime;

    static $inject = ["$q","$http", "$rootScope", "$ocLazyLoad"];
    constructor(private $q: any, private $http: any, private $rootScope: any, private $ocLazyLoad: any) {
        this.runtime = null;
    }

    getRuntime() {
        if (!this.runtime) {
            console.log("Creating Module Federation runtime instance...");
            this.runtime = createInstance({
                name: 'eCareVault',
                remotes: [],
            });
        }
        return this.runtime;
    }

    async registerRemotes(remotes: { name: string; entry: string }[]) {
        if (!this.runtime) {
            this.getRuntime();
        }
        console.log("################### ************** Registering new remotes...", remotes.map(r => r.name).join(", "));
        return await this.runtime.registerRemotes(remotes);
    }

    getRemotesVersion(): any {
        const deferred = this.$q.defer();
        let remotesVersionInfo = this.$rootScope.remotesVersionInfo;
        if (remotesVersionInfo) {
            deferred.resolve(remotesVersionInfo);
        } else {
            this.$http.get(window['ecvConfig'].remotesVersionUrl + "?v=" + Date.now()).then((response => {
                deferred.resolve(response.data);
                this.$rootScope.remotesVersionInfo = response.data;
            }));
        }
        return deferred.promise;
    }

    loadRemoteModule = (moduleName: string, exposedModule: string, ecvModule: string) => {
        //Fetch latest version of remotes from blob storage and construct the remote entry url accordingly, then register and load the remote module
        return this.getRemotesVersion().then((response) => {
            const remoteVersion = response[moduleName]?.version || '1.0.0';
            const remoteEntry = `${window['ecvConfig'].remotes[moduleName].replace('latest', remoteVersion)}`;
            const runtime = this.getRuntime();
            const remote = {
                name: moduleName,
                entry: remoteEntry,
            };

            // 1. Register the remote with the runtime
            return this.registerRemotes([remote]).then(() => {
                // 2. Explicitly LOAD the remote module. This makes the network request.
                return runtime.loadRemote(exposedModule).then(() => {
                    // 3. Now that the module is loaded, inject it into Angular.
                    return this.$ocLazyLoad.inject(ecvModule);
                }).catch(err => {
                    console.error("Gateway: Lazy loading failed:", err);
                    return this.$q.reject(err);
                });
            });
        });
    }

    loadMultipleRemoteModules = (modules: { moduleName: string; exposedModule: string; ecvModule: string }[]) => {
        return Promise.all(modules.map(({ moduleName, exposedModule, ecvModule }) => this.loadRemoteModule(moduleName, exposedModule, ecvModule)));
    }
}

// Register the service with Angular
angular.module('eCareVault.common.services')
    .service('ModuleFederationService', ModuleFederationService);